import { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useMemo, useState } from 'react';
import {
    BusinessCase,
    GetPositionQuoteResponse,
    GetPositionResponse,
    PRODUCT_TYPES,
    ProjectNumber,
    ProjectNumberSchema,
    SUPPORTED_UNITS,
} from '@schuettflix/interfaces';
import { prefillAutomaticPositionForm } from '@/shared/forms/AutomaticProductsForm/domain/prefillAutomaticPositionForm.ts';
import { FieldValues } from 'react-hook-form';
import { PositionSettingsValues } from '@/modules/position/components/PositionSettings/PositionSettings.tsx';
import { ProcessingType } from '@/shared/models/ContextDataModel/interfaces/enums/processing-type.ts';
import { ContextData, ContextDataInterface } from '@/shared/models/ContextDataModel/ContextData.ts';
import { GetConstructionProjectResponse } from '@/clients/constructionProject/types/constructionProject.ts';
import { getProcessingTypeFromEntity } from '@/shared/utils/getProcessingTypeFromEntity.ts';
import { GetPositionConfigurationResponse } from '@/shared/types/positionV2/api/get-position-configuration.ts';
import { Location, useLocation } from 'react-router';
import { ProductConfiguration } from '../models/ContextDataModel/interfaces/entities/product-configuration';

// These are currently the only static position properties that do not change after the initial creation of a position.
// At the moment the context is limited to these position properties because the client doesn't manage position state
// exclusively via this context. Throughout the `PositionWizard` flow a position gets mutated remotely and then re-
// fetched via the `usePosition` query hook. Hence, we can't safely store a position reference in this context without
// the risk of it getting outdated!
export type Position = Pick<
    GetPositionResponse,
    'id' | 'positionNumber' | 'projectNumber' | 'marketCode' | 'customerInfo'
>;
export type PositionQuote = GetPositionQuoteResponse;

export type PositionBaseFormValues = {
    quantity?: number;
    productName?: string;
    billingUnit?: SUPPORTED_UNITS;
    productType?: PRODUCT_TYPES;
    isStandalone?: boolean;
    disposalPurpose?: 'RECYCLING' | 'DISPOSAL';
    salesforceOpportunityId?: string;
} & Partial<PositionSettingsValues>;

export type PositionContextFormValues = PositionBaseFormValues & FieldValues;

type PositionContext = {
    processingType: ProcessingType;
    setProcessingType: Dispatch<SetStateAction<ProcessingType>>;
    contextData: ContextDataInterface<PositionContextFormValues>;
    projectNumber: ProjectNumber;
} & (
    | {
          isEditing: true;
          positionId: string;
      }
    | {
          isEditing: false;
          positionId: string | undefined;
      }
);

type PositionContextProviderProps = PropsWithChildren & {
    positionV2?: GetPositionConfigurationResponse;
    constructionProject?: GetConstructionProjectResponse;
};

const PositionContext = createContext<PositionContext | null>(null);

export function PositionContextProvider({ children, positionV2, constructionProject }: PositionContextProviderProps) {
    const location: Location<{
        businessCase?: BusinessCase;
        salesforceOpportunityId?: string;
        productConfigurations?: ProductConfiguration[];
    } | null> = useLocation();

    const projectNumber = ProjectNumberSchema.parse(
        positionV2?.customer?.projectNumber ?? constructionProject?.projectCode
    );

    const currentProcessingType =
        getProcessingTypeFromEntity(positionV2) ?? location.state?.productConfigurations?.[0]?.processingType;
    const [processingType, setProcessingType] = useState<ProcessingType>(currentProcessingType ?? 'AUTOMATIC');

    const contextData = useMemo(
        () => {
            const internalContextData = new ContextData<PositionContextFormValues>({
                customerInformation:
                    positionV2?.customer ??
                    (constructionProject && {
                        constructionProjectId: constructionProject.id,
                        userId: constructionProject.responsibleUser.id,
                        organizationId: constructionProject.organizationId,
                        projectNumber: constructionProject.projectCode ?? undefined,
                        manualCostCenter: null,
                    }),
                businessCase: positionV2?.businessCase ?? location.state?.businessCase,
                productConfigurations: positionV2?.productConfigurations ?? location.state?.productConfigurations,
                financeSettings: positionV2?.financeSettings,
                defaultValues: {
                    active: positionV2?.active,
                    validFrom: positionV2?.validFrom,
                    validTo: positionV2?.validTo,
                    note: positionV2?.note,
                    salesforceOpportunityId:
                        positionV2?.salesforceOpportunityId ?? location.state?.salesforceOpportunityId,
                },
            });

            if (positionV2 && processingType === 'AUTOMATIC') {
                internalContextData.setFormData(
                    prefillAutomaticPositionForm(
                        positionV2.businessCase.pricingStrategy,
                        positionV2.productConfigurations
                    )
                );
            }

            return internalContextData;
        },
        // NOTE: positionV2, constructionProject are not stable values,
        // which causes contextData to reinitialise, so we use `positionV2?.id`, `constructionProject?.id` instead
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [constructionProject?.id, positionV2?.id]
    );

    return (
        <PositionContext.Provider
            value={{
                contextData,
                processingType,
                setProcessingType,
                projectNumber,
                ...(positionV2?.id
                    ? {
                          isEditing: true,
                          positionId: positionV2.id,
                      }
                    : {
                          isEditing: false,
                          positionId: positionV2?.id,
                      }),
            }}
        >
            {children}
        </PositionContext.Provider>
    );
}

export function usePositionContext() {
    const data = useContext(PositionContext);
    if (data === null) {
        throw new Error('PositionContextProvider needs to be present in node tree');
    }
    return data;
}
